import React, { useContext, useMemo } from 'react';

import classNames from 'classnames';
import { withPrefix } from 'gatsby';

import { SidebarCategory, SidebarPage } from '../../../types';
import { ReactComponent as ChevronLeft } from '../../assets/images/icons/ChevronLeft.svg';
import { ReactComponent as ChevronRight } from '../../assets/images/icons/ChevronRight.svg';
import { PageContext } from '../../context/PageContext';
import { InternalLink } from '../InternalLink';

import * as classes from './style.module.scss';

interface Props {
  pathname: string;
}

const reducer = (
  acc: SidebarPage[],
  page: SidebarPage | SidebarCategory,
): SidebarPage[] => {
  // recursively collect all pages from nested categories
  if ('pages' in page) {
    const p = page.pages.reduce(reducer, []);
    return [...acc, ...p];
  }

  if ('path' in page) {
    return [...acc, page];
  }

  return acc;
};

export const BottomNavigation: React.FC<Props> = ({ pathname }) => {
  const { sidebarContents } = useContext(PageContext);

  if (!sidebarContents) return null;

  const sidebarPages = sidebarContents
    .reduce((acc: SidebarPage[], content) => {
      if ('path' in content) {
        return [...acc, content];
      }

      if ('pages' in content) {
        return [...acc, ...content.pages.reduce(reducer, [])];
      }

      return acc;
    }, [])
    // remove exernal links
    .filter(page => !page.anchor);

  const pageIndex = useMemo(
    () =>
      sidebarPages.findIndex(page => {
        const prefixedPath = withPrefix(page.path);
        return (
          prefixedPath === pathname ||
          prefixedPath.replace(/\/$/, '') === pathname
        );
      }),
    [pathname],
  );

  const prevPage = sidebarPages[pageIndex - 1];
  const nextPage = sidebarPages[pageIndex + 1];

  return (
    <nav className={classes.root}>
      {prevPage && (
        <InternalLink
          to={prevPage.path}
          className={classNames(classes.link, classes.left)}
        >
          <div className={classes.heading}>Previous</div>
          <div className={classes.title}>
            <ChevronLeft aria-hidden />
            {prevPage.title}
          </div>
        </InternalLink>
      )}
      {nextPage && (
        <InternalLink
          to={nextPage.path}
          className={classNames(classes.link, classes.right)}
        >
          <div className={classes.heading}>Next</div>
          <div className={classes.title}>
            {nextPage.title}
            <ChevronRight aria-hidden />
          </div>
        </InternalLink>
      )}
    </nav>
  );
};
